import React, { Fragment } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Box, FormControlLabel, Switch } from '@mui/material';
import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { useGetProductionReCalculations } from 'services/calculations/production';

import { FieldTable2 } from 'components/_basic/Form/FieldTable2';
import { RadioControlled } from 'components/_basic/Form/Radio';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { Loader } from 'components/_basic/Loader/Loader';
import { CurrencyUnitSelector } from 'components/BusinessToolForm/components/CurrencyUnitSelector/CurrencyUnitSelector';
import { useGetPeriodOptions } from 'components/BusinessToolForm/constants/periods';
import { FullScreenWrapper } from 'components/BusinessToolForm/Sections/FinancialPlan/components/FullScreenWrapper';
import { useUnitChange } from 'components/BusinessToolForm/Sections/FinancialPlan/hooks/useUnitChange';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
  GLOBAL_PRODUCTS_NAME,
  GLOBAL_UNIT_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';
import { PRODUCT_NAMES } from 'components/BusinessToolForm/Sections/Global/general/products';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { IProductVolumesTableProps } from './types';

import styles from './ProductVolumesTable.module.scss';

export const ProductVolumesTable: React.FC<IProductVolumesTableProps> = ({ tableName, fields, disabled }) => {
  const { control, getValues, setValue } = useFormContext();

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });
  const { t } = useTranslation();

  const { id: businessPlanId = '' } = useParams();
  const titleWatchName = concatWithDot(GLOBAL_PRODUCTS_NAME, ':index', PRODUCT_NAMES.NAME);

  const columnsNames = getPeriodColumnsNames(period);
  const { mutateAsync, isPending } = useGetProductionReCalculations();
  const { key } = useUnitChange();
  const periodOptions = useGetPeriodOptions();

  const recalculateHandler = async (rowIndex: number, fieldName: string) => {
    const products = getValues(GLOBAL_PRODUCTS_NAME);
    const { monthlySalesPlanPhysical, annualSalesGrowthRate, productId = '' } = products[rowIndex];
    const tableValues = getValues(tableName);
    const productionData = tableValues[rowIndex];
    const unit = getValues(GLOBAL_UNIT_NAME) ?? DIMENSIONALITY_VALUES.SINGLE;
    const productIndex = fieldName.split('.').at(-1) ?? '';

    if (!productId || !productIndex || !businessPlanId) return;

    const productionRequestData = {
      productId,
      productionVolume: Number(monthlySalesPlanPhysical),
      annualGrowthRate: Number(annualSalesGrowthRate),
      productionData,
      productIndex,
    };

    const params = {
      unit,
      businessPlanId,
      productionRequestData: {
        productionRequestData,
      },
    };
    const { productionData: updatedProductionData } = await mutateAsync(params);
    const updatedTable = tableValues.toSpliced(rowIndex, 1, updatedProductionData);
    setValue(tableName, updatedTable);
  };

  return (
    <FormSectionCard disabled={disabled} title={t('businessOperations.production.tableTitle')} collapsible>
      <FullScreenWrapper title={t('businessOperations.production.title')} disabled={disabled}>
        <Fragment key={key}>
          <Box className={styles.header}>
            <RadioControlled
              id={'BUSINESS_OPERATIONS.production.period'}
              name={GLOBAL_PERIOD_NAME}
              control={control}
              options={periodOptions}
              disabled={disabled}
            />
            <CurrencyUnitSelector disabled={disabled} showCurrency={false} />
          </Box>

          <FormSectionFilledCard className={styles.contentWrapper}>
            {isPending && <Loader className={styles.loader} />}
            <FieldTable2
              rows={fields}
              name={tableName}
              disabled={disabled}
              columnsNames={columnsNames ?? []}
              watchRowTitleName={titleWatchName}
              headerName={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
              reCalculationHandler={recalculateHandler}
              firstColumnWidth={110}
              readonlyFirstCell
            />
          </FormSectionFilledCard>

          <Box className={styles.checkboxContainer}>
            <FormControlLabel
              control={<Switch />}
              disabled={true}
              sx={{ fontSize: 14 }}
              componentsProps={{ typography: { variant: 'body2' } }}
              label={t('businessOperations.production.productionVolumeMatchesSales')}
              checked={true}
            />
          </Box>
        </Fragment>
      </FullScreenWrapper>
    </FormSectionCard>
  );
};
